import head from 'lodash/head';
import { logSafeErrorWithoutCustomerDataWrapper } from '@jira/polaris__lib-logging/src/logSafeErrorWithoutCustomerDataWrapper.tsx';
import type { StandardFieldType as NewStandardFieldType } from '@jira/polaris__domain-field/src/FieldType.types.tsx';
import type {
	ConnectionFieldConfiguration,
	Field,
	FieldConfiguration,
} from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
import type { PolarisPlayKind } from '@jira/polaris__domain-field/src/PolarisPlayContribution.types.tsx';
import type { DynamicFieldFormula } from '@jira/polaris__domain-field/src/DynamicFieldFormula.types.tsx';
import type { FetchResponse } from '@jira/polaris__remote-field/src/FetchResponse.types.tsx';
import {
	DuplicateNameError,
	isDuplicateFieldNameError,
} from '@jira/polaris__lib-errors/src/DuplicateNameError.tsx';
import {
	getEntityLimitError,
	EntityLimitError,
} from '@jira/polaris__lib-errors/src/EntityLimitError.tsx';
import { ENTITY_LIMIT_ERROR_TYPE } from '@jira/polaris__lib-errors/src/LimitType.types.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { GetState, SetState, StoreActionApi } from '@atlassian/react-sweet-state';
import type { Props, State } from './FieldsUpdate.types.tsx';

const createProjectAri = (siteId: string, projectId: undefined | string) =>
	JiraProjectAri.create({ projectId: String(projectId), siteId }).toString();

const updateState = (
	newFields: FetchResponse,
	getState: GetState<State>,
	setState: SetState<State>,
): Field => {
	const state = getState();
	const newState = {
		fields: {
			...state.fields,
			...newFields.fields,
		},
		fieldValueDecorations: {
			...state.fieldValueDecorations,
			...newFields.fieldValueDecorations,
		},
	};
	setState(newState);
	return newFields.fields[Object.keys(newFields.fields)[0]];
};

const handleFetchError = async (err: FetchError, callback: (err: Error) => void): Promise<void> => {
	const { statusCode, originalResponse } = err;
	if (statusCode === 409 && originalResponse) {
		const responseJson = await originalResponse.json();
		const errorString = responseJson?.error;
		if (isDuplicateFieldNameError(errorString)) {
			throw new DuplicateNameError(errorString, [{ error: errorString }], statusCode);
		}

		// for now the entity limit error only relies on the statuscode 409, so this is the only else case currently
		const error = new EntityLimitError(
			errorString,
			[{ error: errorString }],
			statusCode,
			ENTITY_LIMIT_ERROR_TYPE.FIELDS_PER_PROJECT,
		);
		callback(error);
		throw error;
	}

	throw err;
};

export const createField =
	(
		name: string,
		type: NewStandardFieldType,
		configuration?: FieldConfiguration,
		description?: string,
		options?: string[],
	) =>
	async (
		{ getState, setState }: StoreActionApi<State>,
		{ projectId, issueTypeIds, fieldRemote, onFieldUpdateFailed }: Props,
	): Promise<Field> => {
		const issueTypeId = head(issueTypeIds);

		if (issueTypeId === undefined || issueTypeIds === undefined || issueTypeIds.length === 0) {
			throw new Error('cannot create field for unknown issue type');
		}
		if (projectId === undefined) {
			throw new Error('cannot create field for unknown project');
		}

		try {
			const fieldsData = await fieldRemote.createField({
				issueTypeIds,
				name: name.trim(),
				type,
				configuration,
				description,
				options,
			});

			return updateState(fieldsData, getState, setState);
		} catch (err) {
			if (err instanceof FetchError) {
				await handleFetchError(err, onFieldUpdateFailed);
			}
			throw err;
		}
	};

export const createConnectionField =
	(
		name: string,
		type: NewStandardFieldType,
		configuration?: ConnectionFieldConfiguration,
		description?: string,
	) =>
	async ({ dispatch }: StoreActionApi<State>): Promise<Field> => {
		try {
			return await dispatch(
				createField(
					name,
					type,
					{
						issueTypeFilters: configuration?.issueTypeFilters ?? [],
					},
					description,
				),
			);
		} catch (error) {
			if (error instanceof Error) {
				logSafeErrorWithoutCustomerDataWrapper(
					'polaris.error.controllers.field.actions.create-field.connection',
					'Failed to create a connection field',
					error,
				);
			}
			throw error;
		}
	};

export const createCalculatedField =
	(name: string, formula: DynamicFieldFormula, description?: string) =>
	async (
		{ getState, setState }: StoreActionApi<State>,
		{ issueTypeIds, fieldRemote, onFieldUpdateFailed }: Props,
	): Promise<Field> => {
		const issueTypeId = head(issueTypeIds);

		if (issueTypeId === undefined || issueTypeIds === undefined || issueTypeIds.length === 0) {
			throw new Error('cannot create field for unknown issue type');
		}

		try {
			const fieldsData = await fieldRemote.createCalculatedField({
				label: name.trim(),
				formula,
				issueTypeIds,
				description,
			});
			return updateState(fieldsData, getState, setState);
		} catch (err) {
			if (err instanceof FetchError) {
				await handleFetchError(err, onFieldUpdateFailed);
			}
			throw err;
		}
	};

export const createPlayField =
	(
		name: string,
		maxSpend: number,
		kind: PolarisPlayKind = 'PolarisBudgetAllocationPlay',
		description?: string,
	) =>
	async (
		{ getState, setState }: StoreActionApi<State>,
		{ onActionFailed, onFieldUpdateFailed, cloudId, projectId, fieldRemote, issueTypeIds }: Props,
		// oxlint-disable-next-line typescript/no-invalid-void-type
	): Promise<Field | void> => {
		try {
			const issueTypeId = head(issueTypeIds);

			if (issueTypeId === undefined || issueTypeIds === undefined || issueTypeIds.length === 0) {
				throw new Error('cannot create field for unknown issue type');
			}

			const fieldsData = await fieldRemote.createPlayField({
				kind,
				label: name.trim(),
				parameters: { maxSpend },
				project: createProjectAri(cloudId, projectId),
				issueTypeIds,
				description,
			});
			return updateState(fieldsData, getState, setState);
		} catch (err) {
			const error = err instanceof Error ? err : new Error('Unknown error');
			const entityLimitError = getEntityLimitError(error);
			if (entityLimitError) {
				onFieldUpdateFailed(entityLimitError);
			} else {
				onActionFailed(error);
			}
			throw error;
		}
	};

export const updatePlayFieldParameters =
	(id: string, parameters: { maxSpend: number }) =>
	({ getState, setState }: StoreActionApi<State>): void => {
		const { fields } = getState();
		Object.keys(fields).forEach((key) => {
			const field = fields[key];
			if (field.play?.id === id) {
				fields[key] = {
					...field,
					play: {
						...field.play,
						parameters,
					},
				};
			}
		});
		setState({
			fields: { ...fields },
		});
	};
